<template>
    <article>
        <p>
            When you deploy an application to a Kubernetes cluster, one of the essential steps is creating a Service.
            It enables other apps within the cluster or external clients to access this app through the network.
            A Service in Kubernetes is a straightforward abstraction, but like any abstraction, it adds complexity into
            the system and can make troubleshooting more challenging.
        </p>

        <h2>Motivation</h2>

        <p>
            The motivation behind writing this article stems from a specific problem we encountered while
            developing <a href="https://github.com/coroot/coroot" target="_blank">Coroot</a>, an open source observability tool.
            Coroot leverages eBPF to build a Service Map that covers 100% of your system without the need
            of modifying your application code.
        </p>

        <p>
            To build a service map we need to discover how containers in a cluster communicate with each other.
            Coroot's agent captures all outbound TCP connections of every container.
            However, when a container connects to another app through a Kubernetes Service,
            it becomes challenging to accurately determine the destination container address of such connections.
        </p>

        <p>
            In this article, we'll look under the hood at how Kubernetes load balancing works using this
            seemingly simple task as an example.
        </p>

        <h2>Built-in Kubernetes load balancing based on iptables</h2>

        <p>
            Let's deploy an app (nginx) with 2 replicas and a service:
        </p>

        <pre v-hl>
$ kubectl create deployment nginx --image=nginx --replicas=2
deployment.apps/nginx created

$ kubectl expose deployment nginx --port=80
service/nginx exposed

$ kubectl get pods -l app=nginx -o wide
NAME                     READY   STATUS    RESTARTS   AGE   IP           NODE   NOMINATED NODE   READINESS GATES
nginx-748c667d99-pdppx   1/1     Running   0          50s   <span class="marked">10.42.0.12</span>   lab    &lt;none>           &lt;none>
nginx-748c667d99-9h6gr   1/1     Running   0          50s   <span class="marked">10.42.0.11</span>   lab    &lt;none>           &lt;none>

$ kubectl get services -l app=nginx
NAME    TYPE        CLUSTER-IP     EXTERNAL-IP   PORT(S)   AGE
nginx   ClusterIP   <span class="marked">10.43.209.15</span>   &lt;none>        80/TCP    17s

$ kubectl get endpoints -l app=nginx
NAME    ENDPOINTS                     AGE
nginx   10.42.0.11:80,10.42.0.12:80   114s
        </pre>


        <p>Now, let's run another pod and connect to an nginx instance through the service:</p>

        <pre v-hl>
$ kubectl run --rm client -it --image arunvelsriram/utils sh
$ telnet nginx 80
Trying <span class="marked">10.43.209.15</span>... <span class="comment">&larr; Service IP</span>
Connected to nginx.default.svc.cluster.local.
Escape character is '^]'.
        </pre>

        <p>
            From the client pod's perspective, it connected to <var>10.43.209.15:80</var>
        </p>

        <pre v-hl.bash>
$ netstat -an |grep EST
tcp        0      0 10.42.0.19:50856        10.43.209.15:80         ESTABLISHED
        </pre>

        <p>
            We are aware that the client is in fact connected to one of our nginx pods.
            However, the question arises: how did this happen, and how can we determine the specific nginx pod the client
            is connected to?
        </p>

        <p>
            When the service was created, Kubernetes (specifically <var>kube-proxy</var>) established iptables rules
            to distribute traffic randomly among the available nginx pods.
            These rules change the destination IP address of incoming traffic to the IP address of one of the nginx pods.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="800px" class="ma-auto" src="/static/img/blog/service-to-service/iptables.svg" />
        </div>

        <p>
            This load balancing approach relies on the <var>conntrack</var> table (a part of Linux network stack) to
            keep track of the connection states.
            The conntrack table maintains information about established connections, including the source and destination
            IP addresses and ports.
        </p>

        <p>
            Hence, we can identify the translated address of the connection in the conntrack table within the root network namespace on the node:
        </p>

        <pre v-hl>
root@lab:~# conntrack -L |grep 50856
tcp 6 86392 ESTABLISHED src=10.42.0.19 dst=10.43.209.15 sport=50856 dport=80 src=<span class="marked">10.42.0.12</span> dst=10.42.0.19 sport=80 dport=50856 [ASSURED] use=1
        </pre>

        <p>
            When a packet is transmitted from the server to the client, the Linux kernel performs a lookup in the
            conntrack table to identify the corresponding connection.
            This is the reason why the second IP:PORT pair in the table entry appears in reverse order.
        </p>

        <p>
            As you can see, in this particular scenario, the connection was established to <var>10.42.0.12:80</var> (nginx-pod-2).
        </p>

        <h2>Istio Service Mesh</h2>

        <p>
            Now let's see how the same scenario works with Istio Service Mesh.
        </p>

        <p>
            A service mesh achieves better control over service-to-service communications by implementing a dedicated
            infrastructure layer that intercepts and manages network traffic between services.
            It does this by using sidecar proxies, such as Envoy, that are deployed alongside each application instance.
        </p>

        <p>
            As Istio is already installed in the cluster, let's proceed by enabling automatic sidecar injection within our cluster:
        </p>

        <pre v-hl.bash>
$ kubectl label namespace default istio-injection=enabled --overwrite
namespace/default labeled
        </pre>

        <p>Now let's run a client pod and connect to the nginx service:</p>

        <pre v-hl>
$ kubectl run --rm client -it --image arunvelsriram/utils sh
$ telnet nginx 80
Trying <span class="marked">10.43.209.15</span>... <span class="comment">&larr; Service IP</span>
Connected to nginx.default.svc.cluster.local.
Escape character is '^]'.
        </pre>

        <p>
            From the client's perspective, nothing has changed. It is connected to the service IP as before:
        </p>

        <pre v-hl.bash>
$ netstat -an|grep EST
tcp        0      0 10.42.0.19:32840        10.43.209.15:80         ESTABLISHED
        </pre>

        <p>
            The iptables rules continue to exist in the root network namespace, however there is no relevant record in the root conntrack table anymore.
            This occurs because the outbound packets from the client are now intercepted directly within the current network namespace of the pod.
        </p>

        <p>
            When Istio injects a sidecar proxy into a pod, its component, <var>pilot-agent</var>, configures iptables to redirect
            all outbound traffic to the Envoy proxy within the same network namespace for further processing and control.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="800px" class="ma-auto" src="/static/img/blog/service-to-service/istio.svg" />
        </div>

        <p>
            Knowing this, we can locate the relevant conntrack table entry within the network namespace of the client pod:
        </p>

        <pre v-hl>
root@lab:~# nsenter -t <span class="marked">&lt;telnet_pid></span> -n conntrack -L |grep 32840
tcp 6 428676 ESTABLISHED src=10.42.0.19 dst=10.43.209.15 sport=32840 dport=80 src=<span class="marked">127.0.0.1</span> dst=10.42.0.19 sport=15001 dport=32840 [ASSURED] use=1
        </pre>

        <p>
            Now we can see that the actual connection destination is <var>127.0.0.1:15001</var> (envoy).
            As Envoy establishes new connections to the service endpoints, it's not feasible to trace the initial
            connection directly to the final destination.
        </p>

        <p>
            However, from Coroot's perspective, this is not a problem.
            Its agent captures and traces all outbound connections, including those from sidecar proxies.
            As a result, there is no difference on service maps between connections proxied by Envoy and those that are not.
        </p>

        <h2>Cilium as a kube-proxy/iptables replacement</h2>

        <p>
            <a href="https://cilium.io/" target="_blank">Cilium</a> is recognized as one of the most powerful network plugins for Kubernetes.
            It not only provides basic network and security capabilities but also offers an eBPF-based alternative to Kubernetes' default
            iptables-based load balancing mechanism.
        </p>

        <p>
            To set up a k3s cluster with Cilium, I used the following commands:
        </p>

        <pre v-hl>
# curl -sfL https://get.k3s.io | \
  INSTALL_K3S_EXEC='--flannel-backend=none --disable-network-policy --disable traefik <span class="mark">--disable-kube-proxy</span>' \
  sh -

$ helm repo add cilium https://helm.cilium.io/

$ helm repo update

$ helm install cilium cilium/cilium \
  --set k8sServiceHost=&lt;api_server_ip> \
  --set k8sServicePort=6443
  --set global.containerRuntime.integration="containerd" \
  --set global.containerRuntime.socketPath="/var/run/k3s/containerd/containerd.sock" \
  --set <span class="mark">global.kubeProxyReplacement="strict"</span> \
  --namespace kube-system
        </pre>


        <p>
            Now, let's perform out experiment with the nginx service and telnet again:
        </p>


        <pre v-hl>
$ kubectl run --rm client -it --image arunvelsriram/utils sh
$ telnet nginx 80
Trying <span class="marked">10.43.49.44</span>... <span class="comment">&larr; Service IP</span>
Connected to nginx.default.svc.cluster.local.
Escape character is '^]'.
        </pre>

        <p>
            From the client's perspective, nothing has changed. It is connected to the service IP as before:
        </p>

        <pre v-hl.bash>
$ netstat -an|grep EST
tcp        0      0 10.0.0.65:42014         10.43.49.44:80          ESTABLISHED
        </pre>

        <p>
            As we have completely disabled <var>kube-proxy</var>, there is no entry related to this connection in the conntrack table.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="800px" class="ma-auto" src="/static/img/blog/service-to-service/cilium.svg" />
        </div>

        <p>
            Cilium intercepts all traffic directed towards service IPs and distributes it among the corresponding pods at the eBPF level.
            In order to perform reverse network address translation, Cilium maintains its own connection tracking table on top of eBPF maps.
            To access and examine this table, we can use the <var>cilium</var> CLI tool within the cilium pod:
        </p>

        <pre v-hl>
$ kubectl  exec -ti &lt;cilium_pod> -n kube-system -- cilium bpf ct list global|grep 42014
TCP OUT 10.43.49.44:80 -> 10.0.0.65:42014 service expires=30289 RxPackets=0 RxBytes=7 RxFlagsSeen=0x00 LastRxReport=0 TxPackets=0 TxBytes=0 TxFlagsSeen=0x13 LastTxReport=8689 Flags=0x0012 [ TxClosing SeenNonSyn ] RevNAT=6 SourceSecurityID=0 IfIndex=0
TCP OUT 10.0.0.65:42014 -> <span class="marked">10.0.0.80:80</span> expires=8699 RxPackets=3 RxBytes=206 RxFlagsSeen=0x13 LastRxReport=8689 TxPackets=3 TxBytes=206 TxFlagsSeen=0x13 LastTxReport=8689 Flags=0x0013 [ RxClosing TxClosing SeenNonSyn ] RevNAT=6 SourceSecurityID=17722 IfIndex=0
TCP IN 10.0.0.65:42014 -> <span class="marked">10.0.0.80:80</span> expires=8699 RxPackets=3 RxBytes=206 RxFlagsSeen=0x13 LastRxReport=8689 TxPackets=3 TxBytes=206 TxFlagsSeen=0x13 LastTxReport=8689 Flags=0x0013 [ RxClosing TxClosing SeenNonSyn ] RevNAT=0 SourceSecurityID=17722 IfIndex=0
        </pre>

        <p>As seen, in this particular case, our client pod is connected to <var>10.0.0.80:80</var> (nginx-pod-2).</p>

        <p>
            Returning to Coroot's agent, it automatically detects the presence of Cilium in the cluster and leverages
            its conntrack table to accurately determine the actual destination of every connection.
        </p>

        <h2>Bonus track: Docker Swarm</h2>

        <p>
            <b>Kubernetes</b>: My network topology can get pretty complicated!<br>
            <b>Docker Swarm</b>: Hold my beer!
        </p>

        <p>
            Let's explore how Service-to-Service communications work in a Docker Swarm cluster just for fun!
        </p>

        <pre v-hl.yaml>
version: "3.8"

services:
  nginx:
    image: nginx
    ports:
      - target: 80
        published: 80
        protocol: tcp
    deploy:
      mode: replicated
      replicas: 2

  client:
    image: arunvelsriram/utils
    command: ['sleep', '100500']
    deploy:
      mode: replicated
      replicas: 1
        </pre>


        <p>
            By applying this configuration, Docker creates a group of containers and establishes an <a href="https://docs.docker.com/network/drivers/overlay/" target="_blank">overlay network</a> that incorporates a load balancer.
        </p>

        <pre v-hl>
$ docker stack deploy -c demo.yaml demo
Creating network demo_default
Creating service demo_nginx
Creating service demo_client

$ docker ps
CONTAINER ID   IMAGE                        COMMAND                  CREATED        STATUS        PORTS     NAMES
97bf67fc9925   arunvelsriram/utils:latest   "sleep 100500"           10 hours ago   Up 10 hours             demo_client.1.62e6nufzfd4t1g8kde0r40r0h
f0bd5fc75c23   nginx:latest                 "/docker-entrypoint.…"   2 days ago     Up 2 days     80/tcp    demo_nginx.2.c2q5ukp66jawe1z0cuyvcfthr
ed101e5d06f7   nginx:latest                 "/docker-entrypoint.…"   2 days ago     Up 2 days     80/tcp    demo_nginx.1.uqz8vgp8b7lywoyi1f6io56w7

$ docker network inspect demo_default
[
    {
        "Name": "demo_default",
        "Id": "trjfagvgjgu7iwbzp6ivuvcub",
        "Containers": {
            "97bf67fc9925ae55c28b16230defad8afe13e54af7bdc24f5b7e05eb8a8eef14": {
                "Name": "demo_client.1.62e6nufzfd4t1g8kde0r40r0h",
                "EndpointID": "c4af5eb9852e4f2d13086b4ea496bc52cc88e26d5f447f4bf6a7cd1126629d48",
                "MacAddress": "02:42:0a:00:02:09",
                "IPv4Address": "10.0.2.9/24",
                "IPv6Address": ""
            },
            "ed101e5d06f7c8757187f23b84db415627860f346ea6c585a8e2455e102f024b": {
                "Name": "demo_nginx.1.uqz8vgp8b7lywoyi1f6io56w7",
                "EndpointID": "81bb68161d02c55f1315a1b3462805ca21f10892a00c0a494d39fa5ceba81e5b",
                "MacAddress": "02:42:0a:00:02:03",
                "IPv4Address": "10.0.2.3/24",
                "IPv6Address": ""
            },
            "f0bd5fc75c2390dcda9efb9a36f099d668583260b05c48933eca5bd4f0a567b9": {
                "Name": "demo_nginx.2.c2q5ukp66jawe1z0cuyvcfthr",
                "EndpointID": "f5a50e587f467fc02fb22855e298867a0c86f89da4a78f3608ea0324df708190",
                "MacAddress": "02:42:0a:00:02:04",
                "IPv4Address": "10.0.2.4/24",
                "IPv6Address": ""
            },
            <span class="marked">"lb-demo_default": {
                "Name": "demo_default-endpoint",
                "EndpointID": "490a5b5bc526ee4aaca34c5a0d443da324d834b20611127769ee3bcff578e0a7",
                "MacAddress": "02:42:0a:00:02:05",
                "IPv4Address": "10.0.2.5/24",
                "IPv6Address": ""
            }</span>
        },
        ...
    }
]
        </pre>

        <p>
            The load balancer (<var>lb-demo_default</var>) is a dedicated network namespace that has been configured to distribute traffic among containers using <a href="https://en.wikipedia.org/wiki/IP_Virtual_Server" target="_blank">IPVS</a>.
        </p>


        <div class="pa-5 my-6">
            <v-img eager max-width="800px" class="ma-auto" src="/static/img/blog/service-to-service/docker-swarm.svg" />
        </div>


        <p>Now let's run telnet from the client container to the nginx service:</p>

        <pre v-hl>
$ docker exec -ti demo_client.1.62e6nufzfd4t1g8kde0r40r0h telnet nginx 80
Trying <span class="marked">10.0.2.2</span>... <span class="comment">&larr; Service IP</span>
Connected to nginx.
Escape character is '^]'.
        </pre>


        <pre v-hl>
# nsenter -t &lt;telnet_pid> -n netstat -an |grep EST
tcp        0      0 10.0.2.9:51504          <span class="marked">10.0.2.2:80</span>            ESTABLISHED
        </pre>

        <p>
            The IP address <var>10.0.2.2</var> is assigned to the load balancer network namespace:
        </p>

        <pre v-hl>
# nsenter --net=/run/docker/netns/lb_trjfagvgj ip a l
1: lo: &lt;LOOPBACK,UP,LOWER_UP> mtu 65536 qdisc noqueue state UNKNOWN group default qlen 1000
    link/loopback 00:00:00:00:00:00 brd 00:00:00:00:00:00
    inet 127.0.0.1/8 scope host lo
       valid_lft forever preferred_lft forever
47: eth0@if48: &lt;BROADCAST,MULTICAST,UP,LOWER_UP> mtu 1450 qdisc noqueue state UP group default
    link/ether 02:42:0a:00:02:05 brd ff:ff:ff:ff:ff:ff link-netnsid 0
    inet 10.0.2.5/24 brd 10.0.2.255 scope global eth0
       valid_lft forever preferred_lft forever
    inet <span class="marked">10.0.2.2/32</span> scope global eth0
       valid_lft forever preferred_lft forever
    inet 10.0.2.6/32 scope global eth0
       valid_lft forever preferred_lft forever
        </pre>


        <p>
            As IPVS also maintains the connection states in the conntrack table, we can locate the actual destination
            of our connection there:
        </p>

        <pre v-hl>
# nsenter --net=/run/docker/netns/lb_trjfagvgj conntrack -L |grep 51504
tcp 6 431940 ESTABLISHED src=10.0.2.9 dst=10.0.2.2 sport=51504 dport=80 src=<span class="marked">10.0.2.4</span> dst=10.0.2.5 sport=80 dport=51504 [ASSURED] use=1
        </pre>

        <p>
            In this case <var>telnet</var> is connected to <var>10.0.2.4:80</var> (nginx-2).
        </p>

        <p>
            Coroot's agent operates in a similar way.
            It first identifies the appropriate overlay network for each container and then locates the load balancer network namespace.
            Afterwards, it performs a conntrack table lookup to determine the actual destination of a given connection.
        </p>

        <h2>Conclusion</h2>

        <p>
            As we have seen, there are multiple ways to organize Service-to-Service communications in Kubernetes.
            Each approach has its own benefits and drawbacks.
            However, it is essential to understand how your chosen method works and maintain observability of your system.
        </p>

        <p>
            Coroot seamlessly integrates with all of these methods, providing you with a comprehensive map of your services within minutes after installation.
        </p>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>

export default {
    components: {},
}
</script>

<style scoped>
.marked {
    font-weight: 600;
    color: #F98D88;
}

.comment {
    font-weight: 600;
    color: white;
}
</style>
